import { useEffect, useState } from "react";

/** Tiny fetch hook — avoids pulling @tanstack/react-query just for three calls.
 *
 * Uses a per-effect `cancelled` flag instead of a mutable ref. This plays
 * nicely with React 18 StrictMode, which mounts → unmounts → remounts on
 * the first commit; the old ref-based approach left the component stuck in
 * a "not mounted" state after the initial unmount.
 */
export function useApi<T>(fn: () => Promise<T>, deps: unknown[] = []): {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [data, setData]   = useState<T | null>(null);
  const [loading, setL]   = useState(true);
  const [error, setErr]   = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setL(true);
    setErr(null);
    fn()
      .then((d)  => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setErr(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setL(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, loading, error, reload: () => setNonce((n) => n + 1) };
}
